import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { singletonGetter } from "@faire/web--source/common/singletons/getter";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { uuid } from "@faire/web--source/common/uuid";
import { createStoreHook } from "@faire/web--source/ui/hooks/useStore";
import postDwellTime from "@faire/web-api--source/api/dwell-time/post";
import { IDwellTime } from "@faire/web-api--source/indigofair/data/IDwellTime";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";

type DwellId = string;

const EVENT_TIMEOUT_SECONDS = [15, 30, 60];

interface IDwell {
  id: string;
  token: string;
  type: IDwellTime.Type;
  startTimestampMillis: number;
}

/**
 * This Singleton tracks "dwells", which is where a user stays on a particular
 * page on our site for a period of time. The view should let the service
 * know when the page has been started and stopped, and this service takes
 * care of the rest.
 *
 * We only care about specific important pages, such as the Brand page or the
 * Product Details Page, see IDwellTime.Type for details.
 *
 * TODO: We also would like to associate the click that brought the user to
 * the page by integrating with TrackedLink and posting up the requestId with
 * the associated dwell events.
 * @deprecated prefer to use `useDwellService` or `withStores` instead
 */
export class DwellService {
  static get = singletonGetter(DwellService);

  private dwell: IDwell | undefined = undefined;

  private eventCount: number = 0;
  private timeoutHandle: number | undefined = undefined;

  private post = (seconds: number) => {
    postDwellTime({
      token: this.dwell!.token,
      request_id: undefined, // TODO
      dwell_id: this.dwell!.id,
      type: this.dwell!.type,
      time_seconds: seconds,
    });
  };

  private getNextInterval = () => {
    if (this.eventCount === 0) {
      return (EVENT_TIMEOUT_SECONDS[0] ?? 0) * 1000;
    } else {
      return (
        ((EVENT_TIMEOUT_SECONDS[this.eventCount] ?? 0) -
          (EVENT_TIMEOUT_SECONDS[this.eventCount - 1] ?? 0)) *
        1000
      );
    }
  };

  private registerCallback = () => {
    if (this.eventCount < EVENT_TIMEOUT_SECONDS.length) {
      this.timeoutHandle = setTimeoutIfPossible(() => {
        this.timeoutHandle = undefined;

        this.post(EVENT_TIMEOUT_SECONDS[this.eventCount] ?? 0);

        this.eventCount += 1;
        this.registerCallback();
      }, this.getNextInterval());
    }
  };

  start = (token: string, type: IDwellTime.Type): DwellId => {
    if (this.dwell) {
      this.stop();
    }

    this.dwell = {
      id: uuid(),
      token,
      type,
      startTimestampMillis: Date.now(),
    };
    this.registerCallback();

    return this.dwell.id;
  };

  stop = () => {
    if (!isNotUndefined(this.dwell)) {
      return;
    }

    const secondsElapsed = Math.floor(
      (Date.now() - this.dwell.startTimestampMillis) / 1000
    );
    this.post(secondsElapsed);

    this.dwell = undefined;
    this.eventCount = 0;

    if (isNotUndefined(this.timeoutHandle)) {
      getWindow()?.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }
  };
}
export const useDwellService = createStoreHook(DwellService);
